import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";

// Helper function to format date to readable format
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return moment(dateString).format("DD/MM/YYYY");
  } catch (e) {
    return dateString;
  }
};

// Helper function to capitalize and replace underscores with spaces
const formatTextValue = (value) => {
  if (!value || typeof value !== "string") return value;
  return value
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const ExportPhysiotherapyData = ({ jsonData }) => {
  let navigate = useNavigate();
  const [selectedFields, setSelectedFields] = useState([]);

  //   console.log("jsonData", jsonData);

  // List of fields to exclude from the user's selection options
  const excludedFields = [
    "id",
    "_id",
    "createdAt",
    "updatedAt",
    "__v",
    "treatment_center",
    "createdBy",
    "flag_patient",
    "tc_state",
    "tc_detail",
    "id_card",
    "temp_patient_id",
    "review_status",
  ];

  // Flatten the nested objects (physiotherapy_score, patientId, visit_id)
  const flattenedData = useMemo(() => {
    if (!jsonData || jsonData.length === 0) return [];
    return jsonData.map((item) => {
      const flattened = { ...item };

      // Flatten physiotherapy_score object
      if (
        item.physiotherapy_score &&
        typeof item.physiotherapy_score === "object"
      ) {
        const multiValuePhysioFields = [
          "joint",
          "muscles",
          "treatment",
          "position",
          "therapy",
        ];

        Object.keys(item.physiotherapy_score).forEach((key) => {
          let value = item.physiotherapy_score[key];

          // Handle fields that can now have multiple values
          if (multiValuePhysioFields.includes(key)) {
            if (Array.isArray(value)) {
              const formattedValues = value
                .map((entry) => {
                  if (typeof entry === "string") {
                    return formatTextValue(entry);
                  }
                  if (entry && typeof entry === "object") {
                    const text =
                      entry.label || entry.value || entry.name || "";
                    return text ? formatTextValue(String(text)) : "";
                  }
                  return entry != null ? String(entry) : "";
                })
                .filter(Boolean);

              value = formattedValues.join(", ");
            } else if (typeof value === "string") {
              value = formatTextValue(value);
            }
          }

          flattened[key] = value;
        });
        // Remove the nested object
        delete flattened.physiotherapy_score;
      }

      // Flatten patientId object
      if (item.patientId && typeof item.patientId === "object") {
        // Extract patient_name
        if (item.patientId.patient_name) {
          flattened.patient_name = item.patientId.patient_name;
        }
        // Extract and parse gender (it's a JSON string)
        if (item.patientId.gender) {
          try {
            const genderObj = JSON.parse(item.patientId.gender);
            flattened.gender =
              genderObj.label || genderObj.value || item.patientId.gender;
          } catch (e) {
            // If parsing fails, use the value as is
            flattened.gender = item.patientId.gender;
          }
        }
        // Store the patientId string value before deleting the object
        const patientIdValue = item.patientId.patientId;
        // Remove the nested object
        delete flattened.patientId;
        // Set patientId as string if it exists
        if (patientIdValue) {
          flattened.patientId = patientIdValue;
        }
      }

      // Flatten visit_id object
      if (item.visit_id && typeof item.visit_id === "object") {
        // Extract and format date_of_visit
        if (item.visit_id.date_of_visit) {
          flattened.date_of_visit = formatDate(item.visit_id.date_of_visit);
        }
        // Store the visit_id string value before deleting the object
        const visitIdValue = item.visit_id._id;
        // Remove the nested object
        delete flattened.visit_id;
        // Set visit_id as string if it exists
        if (visitIdValue) {
          flattened.visit_id = visitIdValue;
        }
      }

      // Handle emr_id if it's an object
      if (item.emr_id && typeof item.emr_id === "object" && item.emr_id._id) {
        flattened.emr_id = item.emr_id._id;
      }

      // Post-process: Format date_of_visit if it exists (in case it wasn't from visit_id)
      if (
        flattened.date_of_visit &&
        typeof flattened.date_of_visit === "string"
      ) {
        // Only format if it looks like an ISO date string
        if (
          flattened.date_of_visit.includes("T") ||
          flattened.date_of_visit.includes("-")
        ) {
          flattened.date_of_visit = formatDate(flattened.date_of_visit);
        }
      }

      // Post-process: Format physiotherapy fields if they weren't already formatted
      const fieldsToFormat = [
        "joint",
        "muscles",
        "treatment",
        "position",
        "therapy",
      ];
      fieldsToFormat.forEach((field) => {
        const fieldValue = flattened[field];

        // If the value is an array (in case any multi-select values leak through),
        // format each entry and join them into a comma-separated string.
        if (Array.isArray(fieldValue)) {
          const formattedValues = fieldValue
            .map((entry) => {
              if (typeof entry === "string") {
                return formatTextValue(entry);
              }
              if (entry && typeof entry === "object") {
                const text = entry.label || entry.value || entry.name || "";
                return text ? formatTextValue(String(text)) : "";
              }
              return entry != null ? String(entry) : "";
            })
            .filter(Boolean);

          flattened[field] = formattedValues.join(", ");
        } else if (fieldValue && typeof fieldValue === "string") {
          // Only format if it contains underscores (hasn't been formatted yet)
          if (fieldValue.includes("_")) {
            flattened[field] = formatTextValue(fieldValue);
          }
        }
      });

      return flattened;
    });
  }, [jsonData]);

  // Compute fields that are selectable (not in the excluded list)
  const selectableFields = useMemo(() => {
    if (!flattenedData || flattenedData.length === 0) return [];
    return Object.keys(flattenedData[0] || {}).filter(
      (field) => !excludedFields.includes(field)
    );
  }, [flattenedData]);

  // Handle field checkbox changes
  const handleFieldChange = (event) => {
    const field = event.target.name;
    setSelectedFields((prev) =>
      event.target.checked ? [...prev, field] : prev.filter((f) => f !== field)
    );
  };

  const sortFields = (fields) => {
    const priorityFields = [
      //   "patientId",
      "patient_name",
      "gender",
      //   "emr_id",
      //   "visit_id",
      "date_of_visit",
      "joint",
      "muscles",
      "treatment",
      "position",
      "therapy",
      "total_hjhs_scoring",
      "total_fish_scoring",
      "comments",
    ]; // Fields to appear first

    //Sort the fields for selection
    const sortedFields = fields.sort((a, b) => {
      const indexA = priorityFields.indexOf(a);
      const indexB = priorityFields.indexOf(b);

      if (indexA > -1 && indexB > -1) {
        return indexA - indexB; // Both are priority fields, sort among themselves
      } else if (indexA > -1) {
        return -1; // Only A is priority, it should come first
      } else if (indexB > -1) {
        return 1; // Only B is priority, it should come first
      }
      return a.localeCompare(b); // Neither are priority, sort alphabetically
    });
    return sortedFields;
  };

  // Handle Select All checkbox
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedFields(selectableFields);
    } else {
      setSelectedFields([]);
    }
  };

  const formatLabel = (fieldName) => {
    // Replace underscores with spaces
    let formattedLabel = fieldName.replace(/_/g, " ");

    // Capitalize the first word
    formattedLabel = formattedLabel.replace(/^\w/, (c) => c.toUpperCase());

    return formattedLabel;
  };

  //log before download
  const logDownload = () => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/physiotherapy/excel/download`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: { ...jsonData },
    };
    axios(config)
      .then(function (response) {
        toast.success("Document downloaded successfully!");
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  // Generate and download the Excel file with timestamp in filename
  const downloadExcel = () => {
    // logDownload();
    const timestamp = new Date().toISOString().replace(/[\-T:\.Z]/g, "");
    const filename = `physiotherapy_data_${timestamp}.xlsx`;

    // Use flattenedData for export
    const ws = XLSX.utils.json_to_sheet(
      flattenedData.map((item) =>
        selectedFields.reduce(
          (obj, field) => ({ ...obj, [field]: item[field] }),
          {}
        )
      )
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PhysiotherapyData");
    XLSX.writeFile(wb, filename);
  };

  // Check if all selectable fields are selected
  const isAllSelected =
    selectableFields.length > 0 &&
    selectableFields.every((field) => selectedFields.includes(field));

  if (
    !jsonData ||
    jsonData.length === 0 ||
    !flattenedData ||
    flattenedData.length === 0
  ) {
    return (
      <div>
        <h3>No Data Available</h3>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div>
      <h3>Select Fields to Export (Physiotherapy Data):</h3>

      <div className="d-flex justify-content-end">
        <Row>
          <Col xs="auto">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>
        &nbsp;
        <Row>
          <Col xs="auto">
            <Button onClick={downloadExcel} disabled={!selectedFields.length}>
              Download Excel
            </Button>
          </Col>
        </Row>
      </div>
      <div>
        <div>
          <label>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
            />
            &nbsp; Select All
          </label>
        </div>
        <hr />
        <div>
          <Row>
            {sortFields(selectableFields).map((field, index) => (
              <Col key={field} xs={12} md={6} lg={6}>
                <Form.Group controlId={`formCheckbox-${field}`}>
                  <Form.Check
                    type="checkbox"
                    label={formatLabel(field)}
                    name={field}
                    onChange={handleFieldChange}
                    checked={selectedFields.includes(field)}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default ExportPhysiotherapyData;
